import { useMemo, useState, useEffect } from 'react';
import { AnalyticsGatewayV1 } from '@frontend/api-analytics-gateway';
import {
  LocationComparisonTable,
  buildValueWithTrendColumns,
  type ValueWithTrendData,
  type ValueWithTrendColumn,
  useScrollToTop,
} from '@frontend/analytics-shared';
import { APPOINTMENT_TABS } from '../constants';
import { useAppointmentAnalyticsV2ShallowStore } from '../hooks/use-appointment-analytics-v2-store';

type ColumnConfig = ValueWithTrendColumn<LocationRowData>;

const DEFAULT_PAGE_SIZE = 25;

const getColumns = (activeTab: string): ColumnConfig[] => {
  if (activeTab === APPOINTMENT_TABS.UPCOMING) {
    return [
      {
        id: 'scheduledAppointments',
        Header: 'Scheduled',
        accessor: 'scheduledAppointments',
      },
      {
        id: 'confirmedAppointments',
        Header: 'Confirmed',
        accessor: 'confirmedAppointments',
        hideTrend: true,
      },
      {
        id: 'atRiskAppointments',
        Header: 'At-Risk',
        accessor: 'atRiskAppointments',
        hideTrend: true,
      },
    ];
  }
  return [
    {
      id: 'scheduledAppointments',
      Header: 'Scheduled',
      accessor: 'scheduledAppointments',
    },
    {
      id: 'completedAppointments',
      Header: 'Completed',
      accessor: 'completedAppointments',
    },
    {
      id: 'cancelledAppointments',
      Header: 'Cancelled',
      accessor: 'cancelledAppointments',
    },
    {
      id: 'confirmedAppointments',
      Header: 'Confirmed',
      accessor: 'confirmedAppointments',
      hideTrend: true,
    },
    {
      id: 'averageAppointmentsPerDay',
      Header: 'Avg Appointments Per Day',
      accessor: 'averageAppointmentsPerDay',
      hideTrend: true,
    },
  ];
};

interface CompareLocationsTableProps {
  filters: {
    locationId?: string[];
    startDate?: string;
    endDate?: string;
  };
}

interface LocationRowData {
  key: string;
  scheduledAppointments: ValueWithTrendData;
  confirmedAppointments: ValueWithTrendData;
  atRiskAppointments?: ValueWithTrendData;
  completedAppointments?: ValueWithTrendData;
  cancelledAppointments?: ValueWithTrendData;
  averageAppointmentsPerDay?: ValueWithTrendData;
}

export const CompareLocationsTable = ({ filters }: CompareLocationsTableProps) => {
  const { activeTab } = useAppointmentAnalyticsV2ShallowStore('activeTab');
  const [pageConfig, setPageConfig] = useState({ pageNumber: 1, pageSize: DEFAULT_PAGE_SIZE });

  const {
    data: LocationsViewTableData,
    isLoading,
    isFetching,
    hasNextPage,
    fetchNextPage,
  } = AnalyticsGatewayV1.Queries.useGetAppointmentLocationsViewQuery(
    {
      ...filters,
      pageSize: pageConfig.pageSize,
    },
    {
      enabled:
        Object.keys(filters).length > 0 &&
        !!filters.startDate &&
        !!filters.endDate &&
        filters.locationId &&
        filters.locationId?.length > 0,
    }
  );

  // Scroll to top when component mounts
  useScrollToTop();

  // Reset to page 1 when filters change
  useEffect(() => {
    setPageConfig((prev) => ({ ...prev, pageNumber: 1 }));
  }, [filters, pageConfig.pageSize]);

  // Fetch next page when navigating forward to an unloaded page
  useEffect(() => {
    if (!LocationsViewTableData?.pages[pageConfig.pageNumber - 1]) {
      fetchNextPage();
    }
  }, [LocationsViewTableData?.pages, fetchNextPage, pageConfig.pageNumber]);

  const getHasNext = () => {
    const pageData = LocationsViewTableData?.pages[pageConfig.pageNumber - 1]?.data ?? [];
    return hasNextPage && pageData.length === pageConfig.pageSize;
  };

  const rows = useMemo((): LocationRowData[] => {
    return (LocationsViewTableData?.pages[pageConfig.pageNumber - 1]?.data ?? []).map((row: any) => {
      if (activeTab === APPOINTMENT_TABS.UPCOMING) {
        return {
          key: row.key ?? '',
          scheduledAppointments: {
            value: Number(row.scheduledAppointments ?? 0),
            change: Number(row.trends?.scheduledAppointments ?? 0),
          },
          confirmedAppointments: {
            value: Number(row.confirmedAppointments ?? 0),
            change: Number(row.trends?.confirmedAppointments ?? 0),
          },
          atRiskAppointments: {
            value: Number(row.atRiskAppointments ?? 0),
            change: Number(row.trends?.atRiskAppointments ?? 0),
          },
        };
      } else {
        return {
          key: row.key ?? '',
          scheduledAppointments: {
            value: Number(row.scheduledAppointments ?? 0),
            change: Number(row.trends?.scheduledAppointments ?? 0),
          },
          confirmedAppointments: {
            value: Number(row.confirmedAppointments ?? 0),
            change: Number(row.trends?.confirmedAppointments ?? 0),
          },
          completedAppointments: {
            value: Number(row.completedAppointments ?? 0),
            change: Number(row.trends?.completedAppointments ?? 0),
          },
          cancelledAppointments: {
            value: Number(row.cancelledAppointments ?? 0),
            change: Number(row.trends?.cancelledAppointments ?? 0),
          },
          averageAppointmentsPerDay: {
            value: Number(row.averageAppointmentsPerDay ?? 0),
            change: Number(row.trends?.averageAppointmentsPerDay ?? 0),
          },
        };
      }
    });
  }, [LocationsViewTableData, pageConfig.pageNumber, activeTab]);

  const COLUMNS = useMemo(() => getColumns(activeTab), [activeTab]);

  const colConfig = buildValueWithTrendColumns<LocationRowData>(COLUMNS);

  return (
    <LocationComparisonTable<LocationRowData>
      data={rows}
      colConfig={colConfig}
      isLoading={isLoading || isFetching}
      tableInstanceId='compare-locations-table'
      hasNextPage={!!getHasNext()}
      pageConfig={pageConfig}
      onPageChange={(action: 'next' | 'prev') => {
        if (action === 'next') {
          setPageConfig((prev) => ({ ...prev, pageNumber: prev.pageNumber + 1 }));
        } else {
          setPageConfig((prev) => ({ ...prev, pageNumber: prev.pageNumber - 1 }));
        }
      }}
      onPageSizeChange={(num: number) => {
        setPageConfig({ pageNumber: 1, pageSize: num });
      }}
    />
  );
};
